<template>
  <div class="chatbot-settings">
    <!-- Utilisation du composant Notification -->
    <Notification 
      v-model:show="showNotification" 
      :message="notificationMessage" 
      :type="notificationType"
    />

    <header class="content-header">
      <div class="header-title">
        <h1>Paramètres avancés </h1>
        <h3 class="header-title-chatbot">{{ chatbot?.bot_name || 'Chatbot' }}</h3>
        <div class="header-subtitle">CONFIGURATION DÉTAILLÉE DU CHATBOT</div>
      </div>
      <div class="header-actions">
        <button class="back-btn" @click="$router.back()">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M19 12H5M12 19l-7-7 7-7"/>
          </svg>
          <span>Retour</span>
        </button>
      </div>
    </header>

    <div v-if="loading" class="loading-state">
      Chargement des paramètres...
    </div>
    
    <div v-else-if="error" class="error-state">
      {{ error }}
    </div>
    
    <div v-else-if="chatbot && chatbot._id" class="settings-content">
      <!-- Informations générales -->
      <div class="settings-section">
        <h2>Informations générales</h2>
        <div class="settings-grid">
          <div class="avatar-container">
            <div class="avatar-row">
              <label for="avatar-upload" class="avatar-preview" title="Cliquez pour changer l'avatar">
                <img :src="chatbot.avatar === '/avatars/bot1.png' ? 'https://placehold.co/250' : apiBaseUrl+'/public/'+ chatbot.avatar" alt="Avatar du chatbot">
                <div class="avatar-overlay">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                    <polyline points="17 8 12 3 7 8"></polyline>
                    <line x1="12" y1="3" x2="12" y2="15"></line>
                  </svg>
                </div>
              </label>
              <input 
                id="avatar-upload" 
                type="file" 
                accept="image/jpeg,image/jpg,image/png" 
                @change="handleAvatarUpload" 
                style="display: none;"
              >
            </div>
          </div>
          <div class="info-item">
            <span class="label">ID du chatbot</span>
            <div class="copy-value">
              <span class="value">{{ chatbot._id }}</span>
              <button class="copy-btn" @click="copyToClipboard(chatbot._id)" title="Copier">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                  <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                </svg>
              </button>
            </div>
          </div>
          <div class="form-group">
            <label>Nom du chatbot</label>
            <input type="text" v-model="chatbot.bot_name" placeholder="Nom du chatbot">
          </div>
          <div class="form-group">
            <label>Type</label>
            <select v-model="chatbot.type">
              <option value="whatsapp">WhatsApp</option>
              <option value="telegram">Telegram</option>
              <option value="messenger">Messenger</option>
              <option value="web">Web</option>
            </select>
          </div>
          <div class="form-group">
            <label>Statut</label>
            <select v-model="chatbot.status">
              <option value="active">Actif</option>
              <option value="inactive">Inactif</option>
              <option value="maintenance">En maintenance</option>
            </select>
          </div>
          <div class="form-group">
            <label>Description</label>
            <textarea v-model="chatbot.description" placeholder="Description du chatbot"></textarea>
          </div>
          <div class="form-group">
            <label>Email</label>
            <input type="email" v-model="chatbot.email" placeholder="Email">
          </div>
        </div>
      </div>
      
      <!-- Configuration WhatsApp -->
      <div v-if="chatbot.type === 'whatsapp'" class="settings-section">
        <h2>Configuration WhatsApp</h2>
        <div class="settings-grid">
          <div class="form-group">
            <label>Numéro de téléphone affiché</label>
            <PhoneInput
              v-model="chatbot.display_phone_number"
              @country-change="handlePhoneChange"
              placeholder="Numéro de téléphone"
            />
          </div>
          <div class="form-group">
            <label>ID du numéro de téléphone</label>
            <input type="text" v-model="chatbot.phone_number_id" placeholder="ID du numéro de téléphone">
          </div>
          <div class="form-group">
            <label>ID de l'application</label>
            <input type="text" v-model="chatbot.app_id" placeholder="ID de l'application">
          </div>
          <div class="form-group">
            <label>Keyword Webhook</label>
            <div class="copy-input">
              <input type="text" v-model="chatbot.keyword_webhook" placeholder="Keyword Webhook" readonly>
              <button class="copy-btn" @click="copyToClipboard(chatbot.keyword_webhook)" title="Copier">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                  <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                </svg>
              </button>
            </div>
          </div>
          
          <div class="form-group full-width">
            <label>URL Webhook</label>
            <div class="copy-input">
              <input type="text" :value="`https://webhook.peelo.chat/pipeline/whatsapp/${chatbot._id}`" placeholder="URL Webhook" readonly>
              <button class="copy-btn" @click="copyToClipboard(`https://webhook.peelo.chat/pipeline/whatsapp/${chatbot._id}`)" title="Copier">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                  <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                </svg>
              </button>
            </div>
          </div>
          
          <div class="form-group full-width">
            <label>Token WhatsApp</label>
            <div class="copy-input">
              <input :type="showPassword ? 'text' : 'password'" v-model="chatbot.access_token.env.WHATSAPP_TOKEN" placeholder="Token WhatsApp">
              <button class="toggle-password" @click="togglePasswordVisibility" title="Afficher/Masquer">
                <svg v-if="showPassword" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                  <circle cx="12" cy="12" r="3"></circle>
                </svg>
                <svg v-else width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                  <line x1="1" y1="1" x2="23" y2="23"></line>
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>
      
      <div class="settings-actions">
        <button class="cancel-btn" @click="$router.back()">Annuler</button>
        <button class="save-btn" @click="saveSettings">Enregistrer</button>
      </div>
    </div>
    
    <div v-else class="error-state">
      Aucune donnée de chatbot disponible
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import { axiosPeelo } from '@/utils/axios';
import PhoneInput from './PhoneInput.vue';
import Notification from './Notification.vue';
import { apiBaseUrl } from '@/config';

// Stocker axiosPeelo dans une constante pour s'assurer qu'il est accessible partout
const axios = axiosPeelo;

console.log('axiosPeelo ====>',axiosPeelo)
console.log('axios ====>',axios)
const route = useRoute();

const chatbot = ref(null);
const loading = ref(false);
const error = ref(null);
const showPassword = ref(false);
const showNotification = ref(false);
const notificationMessage = ref('');
const notificationType = ref('success');
const phoneNumber = ref('');
const phoneCountry = ref(null);

const formatNumber = (number) => {
  if (number === undefined || number === null) return '0';
  return number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, " ");
};

const showNotificationMessage = (message, type = 'success') => {
  notificationMessage.value = message;
  notificationType.value = type;
  showNotification.value = true;
};

const copyToClipboard = (text) => {
  navigator.clipboard.writeText(text).then(() => {
    showNotificationMessage('Copié dans le presse-papiers !', 'copy');
  }).catch(err => {
    console.error('Erreur lors de la copie:', err);
    showNotificationMessage('Erreur lors de la copie', 'error');
  });
};

const togglePasswordVisibility = () => {
  showPassword.value = !showPassword.value;
};

const handleAvatarUpload = async (event) => {
  
  const file = event.target.files[0];
  
  
  if (!file) {
    
    return;
  }
  
  // Vérifier que c'est bien une image et pas un SVG
  const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png'];
  if (!allowedTypes.includes(file.type)) {
    
    showNotificationMessage('Format non supporté. Veuillez utiliser JPG, JPEG ou PNG');
    return;
  }
  
  try {
    
    //loading.value = true;
    
    // Récupérer le token depuis le localStorage
    const userData = localStorage.getItem('user');
    
    
    if (!userData) {
      console.error('Données utilisateur non trouvées');
      showNotificationMessage('Données utilisateur non trouvées');
      return;
    }
    
    // Parser l'objet JSON et extraire le token
    let token;
    try {
      const userObj = JSON.parse(userData);
      
      
      if (userObj.data && userObj.data.token) {
        token = userObj.data.token;
        
      } else {
        console.error('Structure de données utilisateur invalide');
        showNotificationMessage('Structure de données utilisateur invalide');
        return;
      }
    } catch (parseError) {
      console.error('Erreur lors du parsing des données utilisateur:', parseError);
      showNotificationMessage('Format de données utilisateur invalide');
      return;
    }
    
    // Créer un FormData pour envoyer le fichier
    const formData = new FormData();
    formData.append('avatar', file);
    formData.append('chatbot_id', chatbot.value._id);
   
    // Envoyer la requête pour uploader l'avatar
    
    const response = await axios.post(
      `/upload/chatbot/avatar`,
      formData,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      }
    );
    
    
    
    if (response.data && response.data.avatar) {
      // Mettre à jour l'avatar avec l'URL retournée par le serveur
      
      chatbot.value.avatar = response.data.avatar;
      
      //showNotificationMessage(response.data.message || 'Avatar mis à jour avec succès');
    } else {
      console.error('Format de réponse invalide:', response.data);
      showNotificationMessage('Erreur lors de la mise à jour de l\'avatar');
    }
  } catch (err) {
    console.error('Erreur lors de l\'upload de l\'avatar:', err);
    console.error('Détails de l\'erreur:', err.response ? err.response.data : 'Pas de données de réponse');
    showNotificationMessage('Erreur lors de l\'upload de l\'avatar');
  } finally {
    loading.value = false;
    
  }
};

const handlePhoneChange = (country) => {
  
};

const fetchChatbotDetails = async () => {
  
  
  
  try {
    loading.value = true;
    error.value = null;
    
    
    // Récupérer le token depuis le localStorage
    const userData = localStorage.getItem('user');
    
    
    if (!userData) {
      console.error('Données utilisateur non trouvées');
      error.value = 'Données utilisateur non trouvées';
      return;
    }
    
    // Parser l'objet JSON et extraire le token
    let token;
    try {
      const userObj = JSON.parse(userData);
      
      
      if (userObj.data && userObj.data.token) {
        token = userObj.data.token;
        
      } else {
        console.error('Structure de données utilisateur invalide');
        error.value = 'Structure de données utilisateur invalide';
        return;
      }
    } catch (parseError) {
      console.error('Erreur lors du parsing des données utilisateur:', parseError);
      error.value = 'Format de données utilisateur invalide';
      return;
    }
    
    // Faire la requête pour récupérer les détails du chatbot
    
    
    const response = await axios.get(
      `/get/strong/chatbot/${route.params.id}`,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    
    
    if (response.data && response.data.chatbot) {
      chatbot.value = response.data.chatbot;
      
      
    } else {
      console.error('Format de réponse invalide:', response.data);
      error.value = 'Format de réponse invalide';
    }
  } catch (err) {
    console.error('Erreur lors de la récupération des détails du chatbot:', err);
    console.error('Détails de l\'erreur:', err.response ? err.response.data : 'Pas de données de réponse');
    error.value = err.response?.data?.error || 'Erreur lors de la récupération des détails du chatbot';
  } finally {
    loading.value = false;
    
    
  }
};

const saveSettings = async () => {
  try {
    //loading.value = true;
    
    // Récupérer le token depuis le localStorage
    const userData = localStorage.getItem('user');
    if (!userData) {
      error.value = 'Données utilisateur non trouvées';
      return;
    }
    
    // Parser l'objet JSON et extraire le token
    let token;
    try {
      const userObj = JSON.parse(userData);
      if (userObj.data && userObj.data.token) {
        token = userObj.data.token;
      } else {
        error.value = 'Structure de données utilisateur invalide';
        return;
      }
    } catch (parseError) {
      error.value = 'Format de données utilisateur invalide';
      return;
    }
    
    // Préparer les données à envoyer - envoyer toutes les informations du chatbot
    const chatbotData = { ...chatbot.value };
    
    
    
    // Envoyer la requête de mise à jour à l'endpoint /update/strong/chatbot
    const response = await axios.put(
      `/update/strong/chatbot/${chatbot.value._id}`,
      chatbotData,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    
    
    // Afficher un message de succès
    showNotificationMessage('Paramètres enregistrés avec succès !', 'success');
    
    // Navigate back after successful save
    //window.history.back();
  } catch (err) {
    console.error('Erreur lors de la sauvegarde des paramètres:', err);
    showNotificationMessage(err.response?.data?.error || 'Erreur lors de la sauvegarde des paramètres', 'error');
  } finally {
    loading.value = false;
  }
};

onMounted(() => {
  fetchChatbotDetails();
});
</script>

<style scoped>
.chatbot-settings {
  padding: 24px;
}

.content-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 32px;
}

.header-title h1 {
  font-size: 42px;
  font-weight: 600;
  color: #1A1D1F;
  margin: 0;
  margin-bottom: 8px;
}

.header-title-chatbot {
  font-size: 24px;
  font-weight: 600;
  color: #6F767E;
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  gap: 12px;
}

.header-subtitle {
  color: #6F767E;
  font-size: 15px;
  margin-top: 8px;
  margin-bottom: 0;
  font-weight: 500;
}

.header-title {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.back-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  background: white;
  border: 1px solid #E8ECEF;
  border-radius: 12px;
  color: #1A1D1F;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.back-btn:hover {
  background: #F9FAFB;
  border-color: #D1D5DB;
}

.settings-content {
  max-width: 800px;
}

.settings-section {
  background: white;
  border: 1px solid #E8ECEF;
  border-radius: 12px;
  padding: 24px;
  margin-bottom: 24px;
}

.settings-section h2 {
  font-size: 20px;
  font-weight: 600;
  color: #1A1D1F;
  margin: 0 0 24px 0;
}

.settings-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 24px;
}

.avatar-container {
  display: flex;
  flex-direction: column;
  grid-column: 1;
  grid-row: 1 / span 2;
}

.avatar-row {
  display: flex;
  align-items: center;
  gap: 16px;
}

.avatar-preview {
  width: 120px;
  height: 120px;
  border-radius: 50%;
  overflow: hidden;
  border: 2px solid #E8ECEF;
  flex-shrink: 0;
  position: relative;
  cursor: pointer;
  transition: all 0.2s ease;
}

.avatar-preview:hover {
  border-color: #D1D5DB;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.avatar-preview img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.avatar-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.2s ease;
  color: white;
}

.avatar-preview:hover .avatar-overlay {
  opacity: 1;
}

.avatar-upload {
  display: none;
}

.info-item {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.label {
  font-size: 14px;
  color: #6F767E;
}

.value {
  font-size: 16px;
  font-weight: 500;
  color: #1A1D1F;
}

.value.active {
  color: #1ABB34;
}

.value.inactive {
  color: #FF3B30;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
}

.form-group label {
  font-size: 14px;
  font-weight: 500;
  color: #1A1D1F;
}

.form-group input,
.form-group select,
.form-group textarea {
  padding: 8px 12px;
  border: 1px solid #E8ECEF;
  border-radius: 6px;
  font-size: 14px;
  color: #1A1D1F;
  background: white;
  width: 100%;
  box-sizing: border-box;
}

.form-group textarea {
  height: 100px;
  resize: vertical;
}

.form-group input[type="color"] {
  height: 40px;
  padding: 4px;
}

.copy-input {
  position: relative;
  display: flex;
  width: 100%;
  box-sizing: border-box;
}

.copy-input input {
  padding-right: 40px;
  width: 100%;
  flex: 1;
  box-sizing: border-box;
}

.copy-btn,
.toggle-password {
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  cursor: pointer;
  color: #6F767E;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1;
}

.copy-btn:hover,
.toggle-password:hover {
  color: #1A1D1F;
}

.settings-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 32px;
}

.cancel-btn,
.save-btn {
  padding: 12px 24px;
  border-radius: 12px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.cancel-btn {
  background: white;
  border: 1px solid #E8ECEF;
  color: #1A1D1F;
}

.cancel-btn:hover {
  background: #F9FAFB;
  border-color: #D1D5DB;
}

.save-btn {
  background: #1ABB34;
  border: none;
  color: white;
}

.save-btn:hover {
  background: #159B2B;
}

.loading-state,
.error-state {
  text-align: center;
  padding: 40px;
  color: #6F767E;
}

.error-state {
  color: #FF3B30;
}

@media (max-width: 768px) {
  .chatbot-settings {
    padding: 16px;
  }

  .settings-grid {
    grid-template-columns: 1fr;
  }

  .content-header {
    flex-direction: column;
    gap: 16px;
  }

  .header-title h1 {
    font-size: 32px;
  }
}

.form-group.full-width {
  grid-column: 1 / -1;
}

:deep(.vue-tel-input) {
  border: none !important;
  display: flex !important;
  align-items: center !important;
}

:deep(.vti__dropdown) {
  padding: 0 !important;
  background: transparent !important;
  border: none !important;
  min-width: 100px !important;
}

:deep(.vti__dropdown:hover) {
  background: transparent !important;
}

:deep(.vti__selection) {
  padding: 8px 12px !important;
  background: white !important;
  border: 1px solid #E8ECEF !important;
  border-radius: 6px !important;
  margin-right: 8px !important;
  display: flex !important;
  align-items: center !important;
  gap: 8px !important;
}

:deep(.vti__selection:hover) {
  background: #F9FAFB !important;
}

:deep(.vti__flag) {
  margin-right: 8px !important;
}

:deep(.phone-input) {
  padding: 8px 12px !important;
  border: 1px solid #E8ECEF !important;
  border-radius: 6px !important;
  font-size: 14px !important;
  color: #1A1D1F !important;
  background: white !important;
  width: 100% !important;
  box-sizing: border-box !important;
  flex: 1 !important;
}

:deep(.vti__dropdown-list) {
  background: white !important;
  border: 1px solid #E8ECEF !important;
  border-radius: 6px !important;
  margin-top: 4px !important;
  max-height: 200px !important;
  overflow-y: auto !important;
}

:deep(.vti__dropdown-item) {
  padding: 8px 12px !important;
  display: flex !important;
  align-items: center !important;
  gap: 8px !important;
  cursor: pointer !important;
}

:deep(.vti__dropdown-item:hover) {
  background: #F9FAFB !important;
}

:deep(.vti__dropdown-item.selected) {
  background: #F0F2F5 !important;
}

.copy-value {
  display: flex;
  align-items: center;
  gap: 8px;
}

.copy-value .value {
  font-size: 16px;
  font-weight: 500;
  color: #1A1D1F;
}

.copy-value .copy-btn {
  position: static;
  background: none;
  border: none;
  cursor: pointer;
  color: #6F767E;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  transform: none;
}

.copy-value .copy-btn:hover {
  color: #1A1D1F;
}
</style> 